import { notifications } from '@mantine/notifications';
import { useUpdateTodo } from '../services/useTodoQueries';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

export function useCancelTodo() {
    const { user } = useAuthStore.getState();
    const updateTodo = useUpdateTodo();
    const { t } = useTranslation(['todo', 'common']);

    /**
     * 할 일의 취소/재개 상태를 업데이트합니다.
     * @param todoId 업데이트할 할 일 ID
     * @param updateToCancelled 업데이트할 상태. true = 그만하기, false = 재개 로 업데이트할 것임
     * @param todoTitle 업데이트할 할 일 제목 (알림 전송에 사용)
     * @param completeDate 업데이트할 할 일 완료일 (알림 전송에 사용)
     * @param ownerId 업데이트할 할 일 소유자 ID (알림 전송에 사용)
     * @returns void
     */
    const updateTodoCancelStatus = async ({
        todoId,
        updateToCancelled,
        ownerId,
    }: {
        // todo update
        todoId: string;
        updateToCancelled: boolean;

        // notification
        ownerId: string;
    }) => {
        if (!todoId || !user) {
            console.error('Failed to update todo: TodoId, User is required');
            return;
        }

        const action = updateToCancelled ? t('notification.cancel_action') : t('notification.resume_action');

        const loadingNotificationId = notifications.show({
            title: t('notification.processing_title', { action }),
            message: t('common:notification.message'),
            loading: true,
            autoClose: false,
            withCloseButton: false,
        });

        try {
            // 콜백 없이 mutation 실행
            await updateTodo.mutateAsync({
                id: todoId,
                cancelled_date: updateToCancelled
                    ? new Date().toISOString()
                    : null,
            });

            // 성공 시 notification 업데이트
            notifications.update({
                id: loadingNotificationId,
                title: t('notification.success_title', { action }),
                message: t('notification.success_message', { action }),
                color: 'green',
                loading: false,
                autoClose: 3000,
            });
        } catch (error) {
            console.error('Failed to update todo status: ', error);
            // 오류 발생 시 notification 업데이트
            notifications.update({
                id: loadingNotificationId,
                title: t('notification.error_title', { action }),
                message: t('notification.error_message', { action }),
                color: 'red',
                loading: false,
                autoClose: 5000,
            });
        }
    };

    return {
        updateTodoCancelStatus,
        // 필요시 개별 함수도 제공 가능
        cancelTodo: ({
            todoId,
            ownerId,
        }: {
            todoId: string;
            ownerId: string;
        }) =>
            updateTodoCancelStatus({
                todoId,
                updateToCancelled: true,
                ownerId,
            }),
        reopenTodo: ({
            todoId,
            ownerId,
        }: {
            todoId: string;
            ownerId: string;
        }) =>
            updateTodoCancelStatus({
                todoId,
                updateToCancelled: false,
                ownerId,
            }),
    };
}
